const ORDER_SUCCESS_KEY = 'orderSuccessSnapshot';
const CHECKOUT_COMPLETED_KEY = 'checkoutCompleted';

/**
 * Persist placed-order details for the confirmation page (session-scoped).
 */
export function saveOrderSuccessSnapshot(snapshot) {
    if (!snapshot) return;
    try {
        sessionStorage.setItem(ORDER_SUCCESS_KEY, JSON.stringify(snapshot));
    } catch (_e) {
        /* quota / private mode */
    }
}

export function loadOrderSuccessSnapshot() {
    try {
        const raw = sessionStorage.getItem(ORDER_SUCCESS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
}

export function clearOrderSuccessSnapshot() {
    try {
        sessionStorage.removeItem(ORDER_SUCCESS_KEY);
    } catch (_e) {
        /* ignore */
    }
}

/** Set after order is placed so checkout pages cannot be revisited. */
export function markCheckoutCompleted() {
    try {
        sessionStorage.setItem(CHECKOUT_COMPLETED_KEY, '1');
    } catch (_e) {
        /* ignore */
    }
}

export function isCheckoutCompleted() {
    try {
        return sessionStorage.getItem(CHECKOUT_COMPLETED_KEY) === '1';
    } catch (_e) {
        return false;
    }
}

export function clearCheckoutCompleted() {
    try {
        sessionStorage.removeItem(CHECKOUT_COMPLETED_KEY);
    } catch (_e) {
        /* ignore */
    }
}

/** Allow checkout again when starting cart / wishlist → order-summary. */
export function beginCheckoutSession() {
    clearCheckoutCompleted();
}

function hasActiveCheckoutDraft() {
    try {
        const isGuest = localStorage.getItem('guestUser') === 'true';
        const key = isGuest ? 'guestCheckoutData' : 'latestOrder';
        return Boolean(localStorage.getItem(key));
    } catch (_e) {
        return false;
    }
}

/** True when user finished a prior order and has no new checkout draft. */
export function shouldBlockCheckoutRevisit() {
    return isCheckoutCompleted() && !hasActiveCheckoutDraft();
}

/** Remove in-progress checkout payloads after order is placed. */
export function clearCheckoutDraftStorage() {
    try {
        localStorage.removeItem('latestOrder');
        localStorage.removeItem('guestCheckoutData');
    } catch (_e) {
        /* ignore */
    }
}

/** Call when navigating to order success after payment (do not mark completed here — OrderSuccess mounts first). */
export function finalizeCheckoutAndGoToSuccess(navigate, snapshot) {
    clearCheckoutDraftStorage();
    saveOrderSuccessSnapshot(snapshot);
    navigate('/order-success', { replace: true, state: { order: snapshot } });
}

/** Prefer navigation state, then session snapshot. */
export function resolveOrderSuccessPayload(locationState) {
    if (locationState?.order) {
        return locationState.order;
    }
    return loadOrderSuccessSnapshot();
}

export function formatOrderDisplayId(order) {
    if (order?.order_number) return order.order_number;
    const id = order?.orderNumber || order?._id || order?.order_id;
    if (!id) return null;
    const s = String(id);
    if (s.startsWith('guest-')) {
        return s.slice(-12).toUpperCase();
    }
    if (s.length > 8) {
        return s.slice(-8).toUpperCase();
    }
    return s.toUpperCase();
}

export function buildOrderSuccessSnapshot({
    orderData,
    orderId,
    deliveryAddress,
    isGuest = false,
    couponCode = '',
    finalAmount,
    couponDiscount = 0,
}) {
    const summary = orderData?.order_summary || {};
    return {
        _id: orderId || orderData?._id,
        order_number: orderData?.order_number || null,
        orderNumber: orderData?.order_number || orderId || orderData?._id,
        order_items: orderData?.order_items || [],
        order_summary: {
            ...summary,
            coupon_discount: couponDiscount ?? summary.coupon_discount ?? 0,
            final_amount: finalAmount ?? summary.final_amount ?? 0,
        },
        coupon_code: couponCode || orderData?.coupon_code || '',
        deliveryAddress: deliveryAddress || null,
        isGuest: Boolean(isGuest),
        placedAt: orderData?.createdAt || new Date().toISOString(),
    };
}
